import { useEffect, useState, Fragment } from 'react';
import { Button, Checkbox, Divider, FormControlLabel, FormHelperText, TextField, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import CircularLoader from 'ui-component/CircularLoader';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';
import { IconAlertCircle, IconHelp } from '@tabler/icons-react';

/**
 * @params
 *  isOpen (Boolean) = set modal open
 * onClose (Function) = set parent toggle to falsess
 */
export default function ConfirmationModal({ data, onClose }) {
    const [open, setOpen] = useState(false);
    const {
        isOpen,
        title,
        message,
        handleProceed,
        isProceed,
        fnClose,
        content,
        type = 'confirmation',
        confirmToProceed = false,
        confirmToProceedLabel,
        isShowReason = false,
        showReasonPlaceHolder = 'Reason'
    } = data;
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    const modalTheme = {
        cancel: {
            primary: '#f65f5f',
            hover: '#f65f5feb'
        },
        proceed: {
            primary: '#2ca58d',
            hover: '#298270'
        }
    };

    let colorVariant;
    let ConfirmationIcon = IconHelp;
    switch (type) {
        case 'confirmation':
            colorVariant = modalTheme['proceed'];
            break;
        case 'confirmDelete':
            colorVariant = modalTheme['cancel'];
            ConfirmationIcon = IconAlertCircle;
            break;
        default:
            colorVariant = modalTheme['proceed'];
            break;
    }

    useEffect(() => {
        setLoading(isProceed);
    }, [isProceed]);

    useEffect(() => {
        setOpen(isOpen || false);
        if (!(isOpen || false)) {
            setLoading(false);
            setConfirm(false);
            setReason('');
            setError('');
        }
    }, [isOpen]);

    const handleClose = () => {
        setOpen(false);
        setConfirm(false);
        setReason('');
        setError('');
        onClose();

        if (typeof fnClose !== 'undefined') {
            fnClose(); // handle from your component if cancel was click
        }
    };

    return (
        <Fragment>
            <Dialog
                open={open || false}
                size="small"
                // onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                sx={{
                    '.MuiPaper-root': {
                        p: 2,
                        width: '22rem',
                        borderTop: `7px solid ${colorVariant.primary}`,
                        minWidth: '28rem'
                    }
                }}
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" component="div" sx={{ textAlign: 'center', gap: '1.5rem' }}>
                        <ConfirmationIcon size={75} color={colorVariant.primary} />
                        <Box
                            sx={{
                                textAlign: 'center'
                            }}
                        >
                            {title && (
                                <Typography
                                    sx={{
                                        mt: 1.2,
                                        textAlign: 'center',
                                        fontWeight: 450,
                                        fontSize: '1.25rem'
                                    }}
                                >
                                    {title}
                                </Typography>
                            )}
                            {message && (
                                <Typography
                                    sx={{
                                        textAlign: 'center',
                                        lineHeight: 1.2,
                                        fontSize: '0.9rem',
                                        mt: 0.5,
                                        fontWeight: 350
                                    }}
                                    dangerouslySetInnerHTML={{ __html: message }}
                                ></Typography>
                            )}
                            {content}

                            {error && (
                                <Box sx={{ pt: 2 }}>
                                    <FormHelperText error>{error}</FormHelperText>
                                </Box>
                            )}

                            {isShowReason && (
                                <Box sx={{ pt: 2 }}>
                                    <TextField
                                        fullWidth
                                        value={reason}
                                        onChange={(e) => {
                                            setReason(e.target.value);
                                        }}
                                        multiline
                                        rows={3}
                                        placeholder={`Input ${showReasonPlaceHolder}`}
                                    />
                                </Box>
                            )}

                            {confirmToProceed && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: type === 'confirmation' ? 'center' : 'flex-start',
                                        mt: 2
                                    }}
                                >
                                    <FormControlLabel
                                        sx={{ mr: 0 }}
                                        control={<Checkbox checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />}
                                        label={<Typography variant="body2">{confirmToProceedLabel || 'Accept and continue.'}</Typography>}
                                    />
                                </Box>
                            )}
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <Divider sx={{ my: 1.2 }} />
                <DialogActions
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        alignItems: 'center',
                        mt: 1
                    }}
                >
                    <Button
                        onClick={() => {
                            // if isShow Remarks
                            if (isShowReason && reason === '') {
                                setError(`Please Input ${showReasonPlaceHolder} First`);
                            } else {
                                setLoading(true);
                                handleProceed(reason);
                                setReason('');
                            }
                        }}
                        autoFocus
                        variant="contained"
                        size="medium"
                        // fullWidth
                        sx={{
                            background: modalTheme['proceed'].primary,
                            '&:hover': {
                                background: modalTheme['proceed'].hover
                            }
                        }}
                        disabled={(confirmToProceed && !confirm) || loading}
                    >
                        {loading ? (
                            <>
                                <CircularLoader size={24} minHeight="0rem" />
                                <Box paddingLeft={1}>Submitting</Box>
                            </>
                        ) : (
                            'Proceed'
                        )}
                    </Button>
                    <Button
                        onClick={handleClose}
                        autoFocus
                        variant="contained"
                        size="medium"
                        // fullWidth
                        sx={{
                            background: modalTheme['cancel'].primary,
                            '&:hover': {
                                background: modalTheme['cancel'].hover
                            }
                        }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
}

ConfirmationModal.propTypes = {
    data: PropTypes.any,
    onClose: PropTypes.func
};
